import { useRightDrawer } from "@/contexts/RightDrawerContext";
import { useTenantMemberships } from "@/contexts/TenantContext";
import { Colors } from "@/enums/Colors";
import { Spinner } from "@/external/ensolvers-core-frontend-mui-v5/components/Common/Spinner";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import { LazoChatConversation } from "@/models/ai-chat/LazoChatConversation";
import LazoChatConversationService from "@/services/ai-chat/LazoChatConversationService";
import DateGroups from "@/utils/DateGroup";
import { useConversationTypeIdentifier } from "@/utils/LocationUtils";
import { Box, Divider, styled } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChatsCategory } from "./LazoAIChatRightContainerWrapper";
import ConversationDrawerButtons from "./drawer-components/ConversationDrawerButtons";

interface ConversationListProps {
  chatsCategory: ChatsCategory;
  archivedList?: boolean;
  loading: boolean;
}

const CustomDivider = styled(Divider)(() => ({
  color: Colors.LIGHT_GRAY_DEDEE1,
  height: "1px",
  marginBottom: "1rem",
}));

function ConversationsList(props: ConversationListProps) {
  const { t } = useTranslation("chat");
  const convType = useConversationTypeIdentifier() || null;

  const { currentTenantMembership } = useTenantMemberships();

  const [groupedConversations, setGroupedConversations] = useState<(LazoChatConversation | { dateGroupName: string })[]>([]);

  /*const { archivedConversations } = useConversations();*/
  const [chatConversations, setChatConversations] = useState<LazoChatConversation[]>([]);
  const [archivedChatConversations, setArchivedChatConversations] = useState<LazoChatConversation[]>([]);

  const [shouldFetch, setShouldFetch] = useState<Date | undefined>();
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const pageSize = 15;
  const [hasMore, setHasMore] = useState(true);

  const observer = useRef<IntersectionObserver | null>(null);

  const lastConversationRef = useCallback(
    (convRef: Element) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          setPage((prevPage) => prevPage + 1);
        }
      });
      if (convRef) observer.current.observe(convRef);
    },
    [loading]
  );

  const { entityExternalId: contextEntityExternalId } = useRightDrawer();

  const [firstRender, setFirstRender] = useState<boolean>(true);
  useEffect(() => {
    if (firstRender) {
      setFirstRender(false);
      return;
    }
    if (!hasMore) return;

    const tenantId = currentTenantMembership.tenant.externalId;
    setLoading(true);
    LazoChatConversationService.getByTypePaged(
      convType,
      tenantId,
      shouldFetch ? 0 : page,
      shouldFetch ? pageSize * page + pageSize : pageSize,
      contextEntityExternalId
    ).then((r) => {
      r.onSuccess((convs) => {
        shouldFetch ? setChatConversations(convs.content) : setChatConversations((prevState) => [...prevState, ...convs.content]);
        if (convs.page.totalElements <= (page + 1) * pageSize) setHasMore(false);
        setLoading(false);
      });

      r.onError(({}) => {
        (NotificationService.error(t("conversationsList.errors.getConversations")), setLoading(false));
      });
    });
  }, [firstRender, page, shouldFetch]);

  useEffect(() => {
    if (props.archivedList) {
      LazoChatConversationService.getArchivedByUser()
        .then((r) => {
          const list = r.getContentOrThrowError();
          setArchivedChatConversations(list);
        })
        .catch((e) => {
          NotificationService.error(e.message);
        });
    }
  }, [convType, currentTenantMembership?.tenant, props.archivedList]);

  useEffect(() => {
    const sourceConversations = props.archivedList ? archivedChatConversations : chatConversations;

    const filteredConversations = sourceConversations.filter((c) => c.isOwner === (props.chatsCategory === ChatsCategory.MY_CHATS));

    const groupedConversations = DateGroups.createInternationalized<LazoChatConversation>(filteredConversations, t);
    setGroupedConversations(groupedConversations.toCompactArray());
  }, [chatConversations, archivedChatConversations, props.archivedList, props.chatsCategory, t]);

  if (props.loading) {
    return <Spinner variant="indeterminate" sx={{ color: Colors.LAZO_PURPLE }} />;
  }

  function fireFecth() {
    setShouldFetch(new Date());
  }

  return (
    <>
      {groupedConversations.map((item, idx) => {
        if ((item as any).dateGroupName) {
          const dateGroupName = (item as any).dateGroupName;
          return (
            <Box
              key={idx}
              sx={{
                padding: "1rem 2.2rem",
                fontSize: "1rem",
                fontWeight: "500",
                color: Colors.DARKER_GRAY,
              }}
            >
              {idx !== 0 && <CustomDivider orientation="horizontal" />}
              {dateGroupName}
            </Box>
          );
        } else {
          return (
            <Box key={idx} sx={{ padding: "0 1.2rem" }} ref={groupedConversations.length - 1 === idx ? lastConversationRef : null}>
              {groupedConversations.length - 1 === idx && loading ? (
                <Spinner variant="indeterminate" sx={{ color: Colors.LAZO_PURPLE, marginTop: "-3rem" }} />
              ) : (
                <ConversationDrawerButtons conversation={item as LazoChatConversation} optionalFunction={fireFecth} />
              )}
            </Box>
          );
        }
      })}
    </>
  );
}

export default ConversationsList;
